import { FREE_USER_MESSAGE_SEND_LIMIT } from '@mm/constants/message-send-limits';
import { abbreviateNumber, centsToDollars } from '@mm/utils';
import { includes, isNumber } from 'lodash';
import moment from 'moment/moment';
import {
  BasicPage,
  EnhancedBillingPage,
  PageBillingType,
} from '../interfaces/connected-page';
import {
  EnhancedPlan,
  PlanTypeBE,
  SubscriptionResponse,
} from '../interfaces/subscription';
import { SubscriptionModel } from '../model/SubscriptionModel';

export type TeamMessageStatistics = {
  messagesLimit: number;
  used: number;
};

export type TabDetails = {
  name: string;
  billingPeriodPrice?: string;
  sendAllotment?: string;
  billedWith?: string;
  nextBillingDate?: string;
  extraPages?: number;
  teamMessageStatistics?: TeamMessageStatistics;
  sendAllotmentNumber?: number;
};

export type SubscriptionTabDefinition = {
  id: string;
  shortName: string;
  planCode: string;
  pages: EnhancedBillingPage[];
  outgoingMessagesLimit?: number;
  downgradePlansIds: number[];
  upgradePlansIds: number[];
  showPromotionsList: boolean;
  teamSubscription?: SubscriptionModel;
  plan?: EnhancedPlan;
  tabDetails: TabDetails;
};

const FREE_SUBSCRIPTION_PLAN_CODE = 'free';
export const PROMOTION_SUBSCRIPTION_PLAN_CODE = 'subscription';
export const TEAM_TRIAL_SUBSCRIPTION_PLAN_CODE = 'team_subscription';

const getSendAllotment = (outgoingMessagesLimit?: number) => {
  if (outgoingMessagesLimit) {
    const totalSends = abbreviateNumber(outgoingMessagesLimit);

    return `${totalSends} credits`;
  }
};

const getTeamMessageStatistics = (
  plan: EnhancedPlan,
  pages: EnhancedBillingPage[],
) => {
  // Only show for plans with limit
  if (
    plan.outgoingMessagesLimit !== null &&
    [PlanTypeBE.team, PlanTypeBE.teamSpecial, PlanTypeBE.teamCustom].indexOf(
      plan.planType,
    ) >= 0
  ) {
    const firstPage = pages[0];
    const used = firstPage ? firstPage.messagesStatistic.count : 0;

    const outgoingMessagesLimit =
      firstPage && firstPage.subscription
        ? plan.outgoingMessagesLimit < firstPage.subscription.maxSendAllotment
          ? firstPage.subscription?.maxSendAllotment
          : plan.outgoingMessagesLimit
        : plan.outgoingMessagesLimit;
    return {
      messagesLimit: outgoingMessagesLimit,
      used,
    };
  }
};

const getTabDetailsForPlan = (
  plan: EnhancedPlan,
  accountDetails: SubscriptionResponse | undefined,
  teamSubscription: SubscriptionModel | undefined,
  pages: EnhancedBillingPage[],
): TabDetails => {
  const lastCardNumbers =
    accountDetails && accountDetails.account
      ? accountDetails.account.lastCardNumbers
      : 'XXXX';
  const billedWith = teamSubscription
    ? `XXXX-XXXX-XXXX-${lastCardNumbers}`
    : undefined;
  const nextBillingDate = teamSubscription
    ? moment(teamSubscription.endsAt).format('MM/DD/YYYY')
    : undefined;
  const teamLike =
    [
      PageBillingType.Team,
      PageBillingType.CustomTeam,
      PageBillingType.LegacyTeam,
      PageBillingType.SpecialTeam,
    ].indexOf(plan.pageBillingType) >= 0;
  const extraPages =
    teamLike &&
    teamSubscription &&
    isNumber(teamSubscription.extraPages) &&
    isNumber(plan.pagesLimit)
      ? teamSubscription.extraPages
      : undefined;
  const teamMessageStatistics = getTeamMessageStatistics(plan, pages);

  return {
    name: plan.name,
    sendAllotment: getSendAllotment(plan.outgoingMessagesLimit),
    sendAllotmentNumber: plan.outgoingMessagesLimit,
    billingPeriodPrice: centsToDollars(plan.unitAmountInCents),
    extraPages,
    billedWith,
    nextBillingDate,
    teamMessageStatistics,
  };
};

const getTeamTrialTab = (
  facebookPages: EnhancedBillingPage[],
): SubscriptionTabDefinition => {
  const hasTrial = isTeamTrial(facebookPages);

  return {
    id: TEAM_TRIAL_SUBSCRIPTION_PLAN_CODE,
    shortName: 'Trial',
    planCode: TEAM_TRIAL_SUBSCRIPTION_PLAN_CODE,
    pages: hasTrial
      ? facebookPages.filter(p => p.redemption && p.redemption.teamTrial)
      : [],
    downgradePlansIds: [],
    upgradePlansIds: [],
    showPromotionsList: false,
    tabDetails: {
      name: 'Trial Plan',
    },
  };
};

const getPromotionTab = (
  facebookPages: EnhancedBillingPage[],
): SubscriptionTabDefinition => {
  const pages = facebookPages.filter(
    page => page.pageBillingType === PageBillingType.Promotion,
  );

  return {
    id: PROMOTION_SUBSCRIPTION_PLAN_CODE,
    shortName: 'Promotions',
    planCode: PROMOTION_SUBSCRIPTION_PLAN_CODE,
    pages,
    downgradePlansIds: [],
    upgradePlansIds: [],
    showPromotionsList: true,
    tabDetails: {
      name: 'Promotions',
    },
  };
};

const getFreeTab = (
  facebookPages: EnhancedBillingPage[],
): SubscriptionTabDefinition => {
  const pages = facebookPages.filter(
    page => page.pageBillingType === PageBillingType.Free,
  );
  const outgoingMessagesLimit = pages[0]
    ? pages[0].messagesStatistic.outgoingMessageLimit
    : FREE_USER_MESSAGE_SEND_LIMIT;

  return {
    id: FREE_SUBSCRIPTION_PLAN_CODE,
    shortName: 'Free',
    planCode: FREE_SUBSCRIPTION_PLAN_CODE,
    pages,
    outgoingMessagesLimit,
    downgradePlansIds: [],
    upgradePlansIds: [],
    showPromotionsList: true,
    tabDetails: {
      name: 'Customers.ai Free Plan',
      sendAllotment: getSendAllotment(outgoingMessagesLimit),
    },
  };
};

const getFirstSubscription = (
  planCode: string,
  subscriptions: SubscriptionModel[],
) => {
  return subscriptions.find(subscription => subscription.planCode === planCode);
};

const getTabsFromPlans = (
  plans: EnhancedPlan[],
  pages: EnhancedBillingPage[],
  subscriptions: SubscriptionModel[],
  accountDetails?: SubscriptionResponse,
): SubscriptionTabDefinition[] => {
  return plans.map(plan => {
    const planPages = pages.filter(page => {
      return (
        page.subscriptions &&
        page.subscriptions.findIndex(sub => sub.planCode === plan.planCode) >= 0
      );
    });
    const isNewTeamPlan =
      plan.planType === PlanTypeBE.team ||
      plan.planType === PlanTypeBE.teamSpecial;
    const teamSubscription = isNewTeamPlan
      ? getFirstSubscription(plan.planCode, subscriptions)
      : undefined;
    const tabDetails = getTabDetailsForPlan(
      plan,
      accountDetails,
      teamSubscription,
      planPages,
    );

    return {
      id: plan.planCode,
      shortName: plan.shortName,
      planCode: plan.planCode,
      pages: planPages,
      outgoingMessagesLimit: plan.outgoingMessagesLimit,
      upgradePlansIds: plan.upgradePlansIds,
      downgradePlansIds: plan.downgradePlansIds,
      showPromotionsList: false,
      teamSubscription,
      plan,
      tabDetails,
    };
  });
};

export const isTeamTrial = (
  pages: BasicPage[] | EnhancedBillingPage[] | undefined,
): boolean => {
  if (pages === undefined) {
    return false;
  }
  return Boolean(
    pages.findIndex(p => p.redemption && p.redemption.teamTrial) >= 0,
  );
};

export const getSubscriptionTabs = (
  facebookPages: EnhancedBillingPage[],
  plans: EnhancedPlan[],
  accountDetails: SubscriptionResponse | undefined,
): SubscriptionTabDefinition[] => {
  const subscriptions =
    accountDetails && accountDetails.subscriptions
      ? accountDetails.subscriptions
      : [];

  const freeTab = getFreeTab(facebookPages);
  const tabsFromPlans = getTabsFromPlans(
    plans,
    facebookPages,
    subscriptions,
    accountDetails,
  );
  const promotionTab = getPromotionTab(facebookPages);
  const teamTrialTab = getTeamTrialTab(facebookPages);

  const subscriptionTabsArray = [
    freeTab,
    ...tabsFromPlans,
    promotionTab,
    teamTrialTab,
  ];

  const activeSubscriptionPlanCodes = subscriptions.map(
    subscription => subscription.planCode,
  );

  // Filter out plan tabs that don't have an active subscription
  return subscriptionTabsArray.filter(subscriptionTab => {
    // Only show tab if has pages if free or promotion as there are no subscriptions for either of these
    if (
      subscriptionTab.id === FREE_SUBSCRIPTION_PLAN_CODE ||
      subscriptionTab.id === PROMOTION_SUBSCRIPTION_PLAN_CODE ||
      subscriptionTab.id === TEAM_TRIAL_SUBSCRIPTION_PLAN_CODE
    ) {
      return !!subscriptionTab.pages.length;
    }

    // Only show the tab if company has active subscription
    return includes(activeSubscriptionPlanCodes, subscriptionTab.id);
  });
};
